/**
 * Integration OAuth Callback Page
 *
 * Handles the redirect from accounting system OAuth flows.
 * This page receives the OAuth code from the provider (e.g., Fortnox)
 * and exchanges it for tokens via the API.
 *
 * Flow:
 * 1. Provider redirects here with ?code=xxx&state=xxx
 * 2. We send code+state to API to exchange for tokens
 * 3. API returns success/error and we display accordingly
 */

import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, ArrowRight, Building2 } from "lucide-react";
import { integrationsApi } from "@/lib/api";

// Provider display names
const PROVIDER_NAMES: Record<string, string> = {
  fortnox: "Fortnox",
  visma: "Spiris",
  bjorn_lunden: "Bjorn Lunden",
  pe_accounting: "PE Accounting",
  bokio: "Bokio",
  speedledger: "SpeedLedger",
};

type CallbackState =
  | { status: "loading" }
  | { status: "exchanging" }
  | { status: "success"; isGuest: boolean; provider: string; companyId?: string; companyName?: string; sessionKey?: string }
  | { status: "error"; title: string; message: string; isGuest?: boolean };

export function IntegrationCallback() {
  const [searchParams] = useSearchParams();
  const { provider: urlProvider } = useParams<{ provider?: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<CallbackState>({ status: "loading" });
  const [countdown, setCountdown] = useState(5);

  // Store redirect destination once when computed - prevents sessionStorage being read multiple times
  const redirectDestinationRef = useRef<string | null>(null);

  // Store returnUrl from API response (as backup to sessionStorage)
  const apiReturnUrlRef = useRef<string | null>(null);

  // Debug: Log sessionStorage state on mount
  console.log("[IntegrationCallback] Component mounted. sessionStorage oauth_return_url:", sessionStorage.getItem("oauth_return_url"));

  // Get parameters from URL
  const code = searchParams.get("code");
  const oauthState = searchParams.get("state");
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  // These are set when API redirects back (after code exchange)
  const success = searchParams.get("success") === "true";
  const isGuestParam = searchParams.get("guest") === "true";
  const sessionKey = searchParams.get("session_key");
  const companyName = searchParams.get("company_name");
  const companyId = searchParams.get("company_id");
  const provider = searchParams.get("provider") || urlProvider;
  const sourceParam = searchParams.get("source"); // e.g., "integration"
  const returnUrlParam = searchParams.get("return_url"); // The URL to return to after OAuth

  // Handle OAuth code exchange
  useEffect(() => {
    async function exchangeCode() {
      // Already processed or processing
      if (state.status !== "loading") {
        return;
      }

      // FIRST: Handle OAuth errors from provider (e.g., invalid_scope, access_denied)
      if (error) {
        let errorTitle = "Anslutningen misslyckades";
        let errorMessage = errorDescription || error;

        if (error === "access_denied") {
          errorTitle = "Åtkomst nekad";
          errorMessage = "Du avbröt anslutningen eller nekade åtkomst till ditt bokföringsprogram.";
        } else if (error === "invalid_scope") {
          errorTitle = "Ogiltig behörighet";
          errorMessage = "Appen har inte rätt behörigheter konfigurerade i Fortnox. Kontakta support.";
        } else if (error === "invalid_state" || error === "Invalid state parameter") {
          errorTitle = "Sessionen har gått ut";
          errorMessage = "Din anslutningssession har gått ut. Försök igen.";
        } else if (error === "server_error") {
          errorTitle = "Serverfel";
          errorMessage = "Ett fel uppstod hos bokföringsleverantören. Försök igen senare.";
        }

        setState({
          status: "error",
          title: errorTitle,
          message: errorMessage,
        });
        return;
      }

      // Handle success from API redirect
      if (success) {
        if (isGuestParam && sessionKey) {
          sessionStorage.setItem("guest_integration_session", sessionKey);
          if (provider) {
            sessionStorage.setItem("guest_integration_provider", provider);
          }
          if (companyName) {
            sessionStorage.setItem("guest_integration_company", companyName);
          }

          setState({
            status: "success",
            isGuest: true,
            provider: provider || "fortnox",
            companyName: companyName || undefined,
            sessionKey,
          });
        } else {
          setState({
            status: "success",
            isGuest: false,
            provider: provider || "fortnox",
            companyId: companyId || undefined,
            companyName: companyName || undefined,
          });
        }
        return;
      }

      // If we have code and state, exchange them for tokens
      if (code && oauthState) {
        setState({ status: "exchanging" });

        try {
          const result = await integrationsApi.exchangeCode(code, oauthState);

          if (result.success && result.data) {
            const data = result.data;

            // Capture returnUrl from API response (backup to sessionStorage)
            if (data.returnUrl) {
              console.log("[IntegrationCallback] API returned returnUrl:", data.returnUrl);
              apiReturnUrlRef.current = data.returnUrl;
            }

            if (data.isGuest && data.guestSessionKey) {
              // Guest flow - store session and show success
              sessionStorage.setItem("guest_integration_session", data.guestSessionKey);
              if (data.provider) {
                sessionStorage.setItem("guest_integration_provider", data.provider);
              }
              if (data.externalCompanyName) {
                sessionStorage.setItem("guest_integration_company", data.externalCompanyName);
              }

              setState({
                status: "success",
                isGuest: true,
                provider: data.provider || "fortnox",
                companyName: data.externalCompanyName,
                sessionKey: data.guestSessionKey,
              });
            } else if (data.integration) {
              // Authenticated user flow
              setState({
                status: "success",
                isGuest: false,
                provider: data.integration.provider,
                companyId: data.integration.companyId,
                companyName: data.externalCompanyName,
              });
            } else {
              setState({
                status: "error",
                title: "Okänt fel",
                message: "Kunde inte slutföra anslutningen.",
              });
            }
          } else {
            setState({
              status: "error",
              title: "Anslutningen misslyckades",
              message: result.error?.message || "Ett fel uppstod vid anslutningen.",
            });
          }
        } catch (err) {
          console.error("Code exchange error:", err);
          setState({
            status: "error",
            title: "Anslutningen misslyckades",
            message: "Kunde inte kommunicera med servern. Försök igen.",
          });
        }
        return;
      }

      // No recognizable parameters - show loading briefly then error
      setTimeout(() => {
        if (state.status === "loading") {
          setState({
            status: "error",
            title: "Ogiltig begäran",
            message: "Saknade parametrar. Försök ansluta igen.",
          });
        }
      }, 2000);
    }

    exchangeCode();
  }, [code, oauthState, success, error, errorDescription, isGuestParam, sessionKey, companyName, companyId, provider, state.status]);

  // Compute and store redirect destination ONCE when state becomes success
  // This prevents the oauth_return_url from being consumed multiple times
  useEffect(() => {
    if (state.status === "success" && !redirectDestinationRef.current) {
      // Helper to append source=integration to a URL if sourceParam is present
      const appendSourceParam = (url: string): string => {
        if (!sourceParam) return url;
        // Don't double-add the param
        if (url.includes("source=integration")) return url;
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}source=${sourceParam}`;
      };

      // FIRST: Always check sessionStorage - this is set by IntegrationSelector before OAuth
      // and is the most reliable way to return to the wizard
      const storedReturnUrl = sessionStorage.getItem("oauth_return_url");

      // Priority 1: sessionStorage (set before OAuth, most reliable for wizard flow)
      if (storedReturnUrl) {
        console.log("[IntegrationCallback] Using sessionStorage returnUrl:", storedReturnUrl);
        sessionStorage.removeItem("oauth_return_url");
        redirectDestinationRef.current = appendSourceParam(storedReturnUrl);
      }
      // Priority 2: API response returnUrl (captured from exchangeCode response)
      else if (apiReturnUrlRef.current) {
        console.log("[IntegrationCallback] Using API response returnUrl:", apiReturnUrlRef.current);
        redirectDestinationRef.current = appendSourceParam(apiReturnUrlRef.current);
      }
      // Priority 3: URL param return_url (legacy, rarely used)
      else if (returnUrlParam) {
        console.log("[IntegrationCallback] Using URL param returnUrl:", returnUrlParam);
        redirectDestinationRef.current = appendSourceParam(returnUrlParam);
      }
      // Priority 4: Default redirects based on user type
      else {
        console.log("[IntegrationCallback] No returnUrl found, using fallback. sourceParam:", sourceParam, "isGuest:", state.isGuest, "companyId:", state.companyId);
        const sourceQuery = sourceParam ? `?source=${sourceParam}` : "";
        if (state.isGuest) {
          redirectDestinationRef.current = `/wizard${sourceQuery}`;
        } else if (state.companyId) {
          // IMPORTANT: If source=integration, this is from the wizard flow
          // We should NOT go to company page - but we don't have the reportId
          // Best we can do is go to the company's reports page with source param
          // so the user can continue from there
          if (sourceParam === "integration") {
            console.warn("[IntegrationCallback] Lost wizard context - no returnUrl available. Redirecting to company page with source=integration");
          }
          redirectDestinationRef.current = `/dashboard/companies/${state.companyId}${sourceQuery}`;
        } else {
          redirectDestinationRef.current = "/dashboard";
        }
      }
      console.log("[IntegrationCallback] Final redirect destination:", redirectDestinationRef.current);
    }
  }, [state, sourceParam, returnUrlParam]);

  // Get the redirect destination - always uses the same logic as auto-redirect
  // Priority: 1. Cached ref, 2. sessionStorage, 3. return_url param, 4. defaults
  const getRedirectDestination = (): string => {
    // First check cached destination (already computed by useEffect)
    if (redirectDestinationRef.current) {
      return redirectDestinationRef.current;
    }

    // Helper to append source=integration to a URL if sourceParam is present
    const appendSourceParam = (url: string): string => {
      if (!sourceParam) return url;
      if (url.includes("source=integration")) return url;
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}source=${sourceParam}`;
    };

    // Check sessionStorage (most reliable for wizard flow)
    const storedReturnUrl = sessionStorage.getItem("oauth_return_url");
    if (storedReturnUrl) {
      return appendSourceParam(storedReturnUrl);
    }

    // Check API response returnUrl
    if (apiReturnUrlRef.current) {
      return appendSourceParam(apiReturnUrlRef.current);
    }

    // Check return_url param from URL (legacy)
    if (returnUrlParam) {
      return appendSourceParam(returnUrlParam);
    }

    // Fall back to defaults
    const sourceQuery = sourceParam ? `?source=${sourceParam}` : "";
    if (state.status === "success") {
      if (state.isGuest) {
        return `/wizard${sourceQuery}`;
      } else if (state.companyId) {
        return `/dashboard/companies/${state.companyId}${sourceQuery}`;
      }
    }
    return "/dashboard";
  };

  // Check if this is a wizard/integration flow (for button text)
  // The API always includes source=integration for OAuth callbacks from the wizard
  const isWizardFlow = (): boolean => {
    // Most reliable: check if sourceParam is "integration" (set by API)
    if (sourceParam === "integration") {
      return true;
    }
    // Fallback: check returnUrl for wizard-related paths
    const dest = getRedirectDestination();
    return dest.includes("/wizard") || dest.includes("/new-report");
  };

  // Handle countdown for success redirect
  useEffect(() => {
    if (state.status === "success" && redirectDestinationRef.current) {
      const destination = redirectDestinationRef.current;
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(destination);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [state, navigate]);

  // Loading state (exchanging code)
  if (state.status === "loading" || state.status === "exchanging") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="text-primary-600 animate-pulse" size={40} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {state.status === "exchanging" ? "Slutför anslutning..." : "Verifierar anslutning..."}
          </h1>

          <p className="text-gray-600 mb-6">
            Vänta medan vi slutför anslutningen till ditt bokföringsprogram.
          </p>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="animate-spin" size={16} />
            <span>Bearbetar...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (state.status === "error") {
    const backDestination = state.isGuest ? "/wizard" : "/dashboard";

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="text-red-600" size={40} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {state.title}
          </h1>

          <p className="text-gray-600 mb-6">
            {state.message}
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate(-2)}
              className="btn btn-primary w-full"
            >
              Försök igen
            </button>
            <button
              onClick={() => navigate(backDestination)}
              className="btn btn-secondary w-full"
            >
              {state.isGuest ? "Tillbaka till start" : "Gå till Dashboard"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  const providerName = PROVIDER_NAMES[state.provider] || state.provider;
  const displayCompanyName = state.companyName || "";

  if (state.isGuest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Anslutningen lyckades!
          </h1>

          <p className="text-gray-600 mb-2">
            {displayCompanyName ? (
              <>
                <strong>{displayCompanyName}</strong> är nu kopplat till <strong>{providerName}</strong>.
              </>
            ) : (
              <>
                Ditt konto är nu kopplat till <strong>{providerName}</strong>.
              </>
            )}
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Nu kan du fortsätta med att skapa din årsredovisning.
          </p>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
            <Loader2 className="animate-spin" size={16} />
            <span>
              Omdirigerar till wizarden om {countdown} sekund{countdown !== 1 ? "er" : ""}...
            </span>
          </div>

          <button
            onClick={() => navigate(getRedirectDestination())}
            className="btn btn-primary w-full flex items-center justify-center gap-2"
          >
            Gå tillbaka till årsredovisningen
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Authenticated user success - check if we should return to wizard or company page
  // Button uses the same getRedirectDestination() function at click time for consistency
  const showWizardText = isWizardFlow();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-green-600" size={40} />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Anslutningen lyckades!
        </h1>

        <p className="text-gray-600 mb-6">
          {displayCompanyName ? (
            <>
              <strong>{displayCompanyName}</strong> är nu kopplat till <strong>{providerName}</strong>.
            </>
          ) : (
            <>
              Ditt företag är nu kopplat till <strong>{providerName}</strong>.
            </>
          )}
          {" "}Du kan nu hämta bokföringsdata direkt från ditt bokföringsprogram.
        </p>

        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
          <Loader2 className="animate-spin" size={16} />
          <span>
            Omdirigerar om {countdown} sekund{countdown !== 1 ? "er" : ""}...
          </span>
        </div>

        <button
          onClick={() => navigate(getRedirectDestination())}
          className="btn btn-primary w-full flex items-center justify-center gap-2"
        >
          {showWizardText ? "Gå tillbaka till årsredovisningen" : "Gå till företaget"}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
